import { action, computed, observable } from "mobx";
import { stringify } from "query-string";

import { compareDatesPredicate } from "@bps/date-time";
import { ContactType } from "@libs/gateways/practice/PracticeGateway.dtos.ts";
import { routes } from "@libs/routing/routes.ts";
import { ContactErrorHandler } from "@shared-types/practice/contact-error-handler.type.ts";
import { DemographicInitialValues } from "@shared-types/practice/demographic-form-values.types.ts";
import { ExportPatientRecordFormValues } from "@shared-types/practice/export-patient-record-form-values.types.ts";
import { OrgUnit } from "@stores/core/models/OrgUnit.ts";

import { Contact } from "./models/Contact.ts";
import { PracOrgUnit } from "./models/PracOrgUnit.ts";
import type { PracticeStore } from "./PracticeStore.ts";

export class PracticeUi {
  constructor(private store: PracticeStore) {}

  @observable
  public recentlyDeletedPatientNoticeId?: string;

  @observable recentlyUpdatedProviderEtag?: string;

  @observable
  public recentlyAddedContactId?: string;

  @observable recentlyUpdatedOrgUnitEtag?: string;

  @observable
  public demographicInitialValues: DemographicInitialValues | undefined;

  @observable
  public selectedEditCard: string | undefined;

  @observable
  public currentContactId: string | undefined;

  @observable
  public newContactType: ContactType | undefined;

  @observable lastServiceETag?: string;

  public contactErrorSubscribers = new Set<ContactErrorHandler>();

  @observable
  isPatientAppointmentsSearchOnFocus: boolean = false;

  @observable showPatientMergeModal: boolean = false;

  @observable onPatientModalClose?: () => void;

  @action
  setShowPatientMergeModal = (value: boolean) => {
    this.showPatientMergeModal = value;
  };

  exportRecordValues?: ExportPatientRecordFormValues;

  setExportRecordValues = (values: ExportPatientRecordFormValues) => {
    this.exportRecordValues = values;
  };

  @observable masterPatient?: Contact;

  @action
  setMasterPatient = (value?: Contact) => {
    this.masterPatient = value;
  };

  @observable showPatMergeConfirmation: boolean = false;

  @action
  setShowPatMergeConfirmation = (value: boolean) => {
    this.showPatMergeConfirmation = value;
  };

  @observable showExportPatientRecordPasswordDialog: boolean = false;

  @action
  setShowExportPatientRecordPasswordDialog = (value: boolean) => {
    this.showExportPatientRecordPasswordDialog = value;
  };

  @observable showExportPatientRecordModal: boolean = false;

  @action
  setShowExportPatientRecordModal = (value: boolean) => {
    this.showExportPatientRecordModal = value;
  };

  @action
  setPatientAppointmentsSearchOnFocus = (value: boolean) => {
    this.isPatientAppointmentsSearchOnFocus = value;
  };

  @action
  public showEditContact = (
    cardId: string,
    id: string,
    onHideModal?: () => void
  ) => {
    this.selectedEditCard = `${cardId}-edit`;
    this.currentContactId = id;
    this.onPatientModalClose = onHideModal;
  };

  @action
  public hideModal = () => {
    if (this.onPatientModalClose) {
      this.onPatientModalClose();
    }

    this.onPatientModalClose = undefined;
    this.currentContactId = undefined;
    this.newContactType = undefined;
    this.selectedEditCard = undefined;
    this.demographicInitialValues = undefined;
  };

  @action
  public setSelectedEditCard = (cardId: string | undefined) => {
    this.selectedEditCard = cardId;
  };

  @action
  public showAddContact = (
    type: ContactType,
    initialValues?: DemographicInitialValues
  ) => {
    this.newContactType = type;
    this.demographicInitialValues = initialValues;
  };

  @action
  showContactDetails = (id: string, section?: string) => {
    this.store.root.routing.pushWithFromQuery({
      pathname: routes.contacts.contact.path({ id }),
      search: stringify({ section })
    });
  };

  public onContactError(handler: ContactErrorHandler) {
    this.contactErrorSubscribers.add(handler);
  }

  fetchAllLocations = async (activeOnly?: boolean): Promise<OrgUnit[]> => {
    const { results: _locations } = await this.store.root.core.loadOrgUnits();
    const locations = activeOnly
      ? _locations.filter(orgUnit => !orgUnit.isInactive)
      : _locations;

    const locationData = await this.store.getAllOrgUnitsLocationData();

    if (locations.length) {
      const orgUnits = locations
        .filter(l => locationData.find(data => data.id === l.id))
        .sort((a, b) =>
          compareDatesPredicate(b.createdLocationDate, a.createdLocationDate)
        );
      await Promise.all(
        orgUnits.map(unit => unit.preloadIsDefaultLocation(unit.id))
      );
      return orgUnits;
    }
    return [];
  };

  @computed
  get defaultPracOrgUnit(): PracOrgUnit | undefined {
    return Array.from(this.store.allOrgUnitsLocationDataMap.values()).find(
      orgUnit => orgUnit.isDefault
    );
  }

  @computed
  get defaultCoreOrgUnit(): OrgUnit | undefined {
    if (!this.defaultPracOrgUnit) return undefined;
    return this.store.root.core.orgUnitsMap.get(this.defaultPracOrgUnit.id);
  }

  loadDefaultOrgUnit = async () => {
    if (this.defaultPracOrgUnit) return;
    await this.fetchAllLocations(true);
  };
}
